import React, { useMemo } from 'react'

import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'
import Tag from 'components_new/atoms/Tag'
import Text from 'components_new/atoms/Text'

import EmptyPlaceholder from 'components_new/molecules/EmptyPlaceholder'
import SectionHeader from 'components_new/molecules/SectionHeader'

import { JobSummary, WorkflowSummary } from 'redux/selectors/internal/DataRuns'
import { jobKey, jobLookupKey, STATUS_COLOR, STATUS_LABEL } from '../utils'

export interface DataRunsWorkflowsProps {
  jobs: JobSummary[]
  onSelectJobByName: (jobName: string, customerId?: string) => void
  onSelectJobRun: (job: JobSummary) => void
  workflows: WorkflowSummary[]
}

/**
 * "Extrakörningar" - one block per Glue workflow that is an extra run on
 * top of the customer's nightly batch. Workflows that *are* a customer's
 * whole run (their jobs have no schedule of their own) are left out by the
 * API; their jobs show up in the other sections like any other job.
 *
 * Included-job chips call
 * `onSelectJobRun` when a matching run exists in the current scope's job
 * list, otherwise `onSelectJobByName` (the workflow graph can include jobs
 * that have not produced a run yet).
 */
const DataRunsWorkflows = (props: DataRunsWorkflowsProps) => {
  const { jobs, onSelectJobByName, onSelectJobRun, workflows } = props

  const jobByKey = useMemo(() => {
    const map = new Map<string, JobSummary>()

    jobs.forEach((job) => map.set(jobKey(job), job))

    return map
  }, [jobs])

  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '20px',
        p: '20px 22px'
      }}
    >
      <SectionHeader
        body="Glue-workflows som körs utöver kundens ordinarie nattkörning"
        icon={
          <Box
            sx={{
              bgcolor: 'secondary.main',
              borderRadius: '2px',
              height: '10px',
              width: '10px'
            }}
          />
        }
        title="Extrakörningar"
        titleAdornment={
          <Chip color="default" label={String(workflows.length)} size="small" />
        }
      />

      {workflows.length === 0 ? (
        <EmptyPlaceholder title="Inga extrakörningar." />
      ) : (
        workflows.map((workflow) => (
          <Box
            key={workflow.name}
            sx={{
              bgcolor: 'secondary.background',
              border: '1px solid',
              borderColor: 'secondary.backgroundDark',
              borderRadius: '14px',
              mt: 1.5,
              p: '14px 16px'
            }}
          >
            <Box
              sx={{
                alignItems: 'center',
                display: 'flex',
                flexWrap: 'wrap',
                gap: 1
              }}
            >
              <Text sx={{ fontWeight: 700 }} variant="subtitle2">
                {workflow.name}
              </Text>
              <Text sx={{ color: 'text.secondary' }} variant="body2">
                {workflow.customer?.name ?? 'Okänd kund'}
              </Text>
              <Box sx={{ flex: 1 }} />
              <Text
                sx={{ color: 'secondary.text', fontWeight: 600 }}
                variant="body2"
              >
                {workflow.schedule_label}
              </Text>
              {workflow.last_run_status ? (
                <Tag
                  color={STATUS_COLOR[workflow.last_run_status]}
                  label={STATUS_LABEL[workflow.last_run_status]}
                />
              ) : null}
            </Box>

            <Text
              component="div"
              sx={{ color: 'text.secondary', mb: 0.5, mt: 1 }}
              variant="body2"
            >
              {workflow.included_jobs.length} jobb ingår
            </Text>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              {workflow.included_jobs.map((includedJob) => {
                // The workflow's own customer first, then the unattributed
                // group: a run the ingest could not tie to a customer has
                // no customer id, and is still the run this chip is about.
                const matchingJob =
                  jobByKey.get(
                    jobLookupKey(includedJob.name, workflow.customer?.id)
                  ) ?? jobByKey.get(jobLookupKey(includedJob.name, null))

                return (
                  <Chip
                    color={
                      includedJob.last_run_status
                        ? STATUS_COLOR[includedJob.last_run_status]
                        : 'default'
                    }
                    key={includedJob.name}
                    label={includedJob.name}
                    onClick={() =>
                      matchingJob
                        ? onSelectJobRun(matchingJob)
                        : onSelectJobByName(
                          includedJob.name,
                          workflow.customer?.id
                        )
                    }
                    size="small"
                  />
                )
              })}
            </Box>

            {workflow.cron ? (
              <Text
                component="div"
                sx={{
                  color: 'text.disabled',
                  fontFamily: 'monospace',
                  fontSize: 12,
                  mt: 1
                }}
              >
                {workflow.cron}
              </Text>
            ) : null}
          </Box>
        ))
      )}
    </Box>
  )
}

DataRunsWorkflows.displayName = 'DataRunsWorkflows'
export default DataRunsWorkflows
